/** @format */
import { type Attributes } from '@opentelemetry/api';
import type { HomrVisualAssets } from './homr/visual-assets.interface';

export interface CriteoTransactionMetadata {
  currency?: string;
  items: string;
  prices: string;
  quantities: string;
  transactionId: string;
}

export interface CriteoTrackTransactionMetadata {
  currency?: string;
  items: string[];
  prices: string[];
  quantities: number[];
  transactionId: string;
}

export interface CriteoTrackTransactionResponse {
  OnLoadBeacon: string;
  placements: [CriteoCarouselPlacement];
}

export interface CriteoViewItemMetadata {
  availability: number;
  category?: string;
  item: string;
  parentItem?: string;
  price: string;
}

export interface CriteoViewProductsMetadata {
  category: string;
  filters?: string;
  item?: string;
  listSize?: number;
  pageNumber?: number;
  parentItem?: string;
  productType: ProductTypes;
}

export enum ProductTypes {
  VIEW_CATEGORY,
  VIEW_SEARCH_RESULT,
}

export interface CriteoViewProductsPlacements {
  carousel: CriteoPlacement[];
  inGrid: CriteoPlacement[];
  digitalShelfTalker?: CriteoDigitalShelfTalker;
  videos?: CriteoVideo[];
}

export interface CriteoViewProductsResponse {
  OnLoadBeacon: string;
  placements: CriteoViewProductsPlacements;
  status?: string;
  errors?: string[];
}

export interface CriteoViewProductsCriteoResponse {
  OnLoadBeacon: string;
  placements: Array<Record<string, CriteoPlacement[] | CriteoDigitalShelfTalker[] | CriteoVideo[]>>;
  status?: string;
  errors?: string[];
}

export interface CriteoViewItemResponse {
  OnLoadBeacon: string;
  placements: CriteoCarouselPlacement[];
}

export interface CriteoInGridPlacement {
  inGrid: CriteoPlacement[];
}

export interface CriteoCarouselPlacement {
  carousel: CriteoPlacement[];
}

export interface CriteoDigitalShelfTalkerPlacement {
  digitalShelfTalker: CriteoDigitalShelfTalker;
}

export interface CriteoDigitalShelfTalker {
  OnClickBeacon: string;
  OnLoadBeacon: string;
  OnViewBeacon: string;
  OnFileClickBeacon: string;
  OnBundleBasketChangeBeacon: string;
  LineItemID: string;
  rendering: CriteoDigitalShelfTalkerRendering;
}

export interface CriteoDigitalShelfTalkerRendering {
  background_image: string;
  header_text: string;
  subcopy_text: string;
  button_text: string;
  redirect_url: string;
  text_color: string;
}

export interface CriteoPlacement {
  OnClickBeacon: string;
  OnLoadBeacon: string;
  OnViewBeacon: string;
  format: string;
  products: CriteoProduct[];
  rendering: string;
}

export interface CriteoProduct {
  AdvertiserId: string;
  ClientAdvertiserId: string;
  ComparePrice: string;
  Image: string;
  LineItemID: string;
  OnBasketChangeBeacon: string;
  OnClickBeacon: string;
  OnLoadBeacon: string;
  OnViewBeacon: string;
  OnWishlistBeacon: string;
  ParentSKU: string;
  Price: string;
  ProductId: string;
  ProductName: string;
  ProductPage: string;
  PromoText: string;
  Rating: string;
  RenderingAttributes: string;
  Shipping: string;
}

/**
 * Criteo product tracking for DSG analytics.
 */
export interface CriteoTrackingItem {
  vendor: string;
  lineItemId: string;
  placements: string;
  slot: number;
}

export interface CriteoPinnedContentSpot {
  altText: string;
  csid: string;
  cta: { link: string; linkTitle: string }[];
  headline: string;
  onClickBeacon: string;
  onViewBeacon: string;
  pinnedPosition: number;
  Subhead: string;
  textColor: string;
  visualAssets: [HomrVisualAssets];
}

export interface PriceObject {
  maxofferprice?: string;
  minofferprice?: string;
  maxlistprice?: string;
  minlistprice?: string;
  maxmapprice?: string;
  minmapprice?: string;
  offerprice?: string;
  mapPriceIndicator?: number;
}

export interface CriteoHoneycombErrorLog {
  name: string;
  message?: string;
  method?: string;
  errorStack?: string;
  metadata?: Attributes;
  errorName?: string;
}

export interface CriteoVideo {
  format: string;
  LineItemID: string;
  OnBundleBasketChangeBeacon: string;
  OnClickBeacon: string;
  OnFileClickBeacon: string;
  OnLoadBeacon: string;
  OnViewBeacon: string;
  TagVideoVAST: string;
  products: CriteoProduct[];
  products_order: CriteoVideoProductOrder[];
  rendering: CriteoVideoRendering;
}

export interface CriteoVideoProductOrder {
  products: string[];
  isMandatory: boolean;
}

export interface CriteoVideoRendering {
  background_video_alt_text: string;
  color_mode: string;
  optional_redirect_target: string;
  optional_legal_text: string;
}

export interface CriteoRenderingAttributes {
  rendering_attributes?: string;
  numberOfReviews?: string;
}
